import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Shield, Server, KeyRound, FileText, CheckCircle2, Eye,
  ArrowRight, ArrowUpRight,
} from "lucide-react";
import Wordmark from "./Wordmark";

const pillars = [
  {
    icon: Server,
    title: "EU Data Residency",
    desc: "All data is stored and processed exclusively on servers within the European Union. No transatlantic transfers by default.",
    badge: "Frankfurt, EU",
    color: "#34D399",
  },
  {
    icon: KeyRound,
    title: "End-to-End Encryption",
    desc: "Data encrypted at rest (AES-256) and in transit (TLS 1.3). Encryption keys are never shared with third parties.",
    badge: "AES-256 · TLS 1.3",
    color: "#60A5FA",
  },
  {
    icon: Shield,
    title: "Row-Level Security",
    desc: "Per-tenant data isolation enforced at the database layer. Your contractor data is never accessible to other organisations.",
    badge: "RLS · RBAC",
    color: "#FF4F00",
  },
  {
    icon: FileText,
    title: "DPA Ready",
    desc: "A GDPR Art. 28-compliant Data Processing Agreement is available for all customers — required for enterprise procurement.",
    badge: "Art. 28 GDPR",
    color: "#FBBF24",
  },
  {
    icon: Eye,
    title: "Audit Logging",
    desc: "Immutable audit trail of all access, changes, and approvals. Know exactly who did what and when — critical for compliance reviews.",
    badge: "Immutable logs",
    color: "#A78BFA",
  },
  {
    icon: CheckCircle2,
    title: "SOC-2 in Progress",
    desc: "Actively pursuing SOC-2 Type II certification. Security controls are already implemented and in continuous review.",
    badge: "In progress",
    color: "#4ADE80",
  },
];

const rights = [
  { right: "Access", desc: "Request a copy of all data we hold about you." },
  { right: "Rectification", desc: "Correct inaccurate or incomplete data at any time." },
  { right: "Erasure", desc: "Request full deletion of your data ('right to be forgotten')." },
  { right: "Portability", desc: "Export your data in machine-readable format." },
  { right: "Objection", desc: "Object to processing based on legitimate interests." },
  { right: "Restriction", desc: "Limit how we process your data in certain cases." },
];

export default function Trust() {
  return (
    <div className="min-h-screen bg-[#060C0C] text-white relative overflow-x-hidden" data-testid="trust-page">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" aria-hidden />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] glow-vermilion opacity-20 pointer-events-none" aria-hidden />

      {/* Nav */}
      <header className="relative z-10 pt-8 pb-4 px-6 lg:px-12 max-w-[1200px] mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <Wordmark size="lg" />
        </Link>
        <a
          href="mailto:support@vawick.de"
          className="text-sm text-[#9BB8B8] hover:text-white transition-colors sm:text-right"
        >
          Questions?{" "}
          <span className="text-[#7A9B9B]">support@vawick.de</span>
        </a>
      </header>

      <main className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 pt-16 pb-24">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-xs text-[#9BB8B8] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#34D399] pulse-dot shrink-0" />
            <span className="font-mono tracking-[0.12em]">GDPR Compliance · EU Data Security</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-[-0.04em] leading-[1.05] mb-5">
            Your data, fully protected.<br />
            <span className="italic font-normal text-[#9BB8B8]">
              By design, not as an afterthought.
            </span>
          </h1>
          <p className="text-[#9BB8B8] text-lg max-w-xl mx-auto leading-relaxed">
            nexus-vs was built for European B2B operations. GDPR compliance, EU data residency,
            and enterprise-grade security are part of the foundation — not bolted on later.
          </p>
        </motion.div>

        {/* Compliance pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="glass rounded-2xl p-6 border border-[#152222] hover:border-[#1F3535] transition-colors"
            >
              <div
                className="w-10 h-10 rounded-xl grid place-items-center mb-5"
                style={{ background: `${p.color}18`, border: `1px solid ${p.color}40` }}
              >
                <p.icon className="w-5 h-5" style={{ color: p.color }} />
              </div>
              <h3 className="font-display text-lg font-medium mb-2">{p.title}</h3>
              <p className="text-sm text-[#9BB8B8] leading-relaxed mb-4">{p.desc}</p>
              <span
                className="text-[10px] font-mono px-2.5 py-1 rounded-full border"
                style={{
                  color: p.color,
                  borderColor: `${p.color}40`,
                  background: `${p.color}10`,
                }}
              >
                {p.badge}
              </span>
            </motion.div>
          ))}
        </div>

        {/* GDPR rights */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass rounded-2xl border border-[#152222] p-8 sm:p-10 mb-10"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3">
            Your rights
          </div>
          <h2 className="font-display text-3xl font-medium tracking-tight mb-2">
            GDPR rights, fully supported.
          </h2>
          <p className="text-[#9BB8B8] mb-8 max-w-lg">
            Every data subject right under GDPR is honoured. Requests are handled within 30 days
            with no friction.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {rights.map((r) => (
              <div key={r.right} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#34D399] mt-1.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-white">{r.right}</div>
                  <div className="text-xs text-[#9BB8B8] mt-0.5 leading-relaxed">{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-[#7A9B9B]">
            To exercise your rights, contact{" "}
            <a
              href="mailto:support@vawick.de"
              className="text-white hover:text-[#FF4F00] transition-colors"
            >
              support@vawick.de
            </a>.
          </p>
        </motion.div>

        {/* CTA cards */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          <Link
            to="/dpa"
            className="group glass rounded-2xl border border-[#152222] hover:border-[#FF4F00]/40 p-6 transition-colors"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3">
              GDPR Art. 28
            </div>
            <div className="font-display text-xl font-medium mb-2">
              Data Processing Agreement
            </div>
            <p className="text-sm text-[#9BB8B8] mb-5">
              Available for all customers. Required for enterprise procurement sign-off.
            </p>
            <div className="flex items-center gap-1.5 text-sm font-medium text-white group-hover:text-[#FF4F00] transition-colors">
              Read DPA{" "}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>

          <Link
            to="/privacy"
            className="group glass rounded-2xl border border-[#152222] hover:border-[#9BB8B8]/30 p-6 transition-colors"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#9BB8B8] font-bold mb-3">
              Privacy
            </div>
            <div className="font-display text-xl font-medium mb-2">Privacy Policy</div>
            <p className="text-sm text-[#9BB8B8] mb-5">
              How we collect, use, and protect personal data across the platform.
            </p>
            <div className="flex items-center gap-1.5 text-sm font-medium text-white group-hover:text-[#9BB8B8] transition-colors">
              Read policy{" "}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>

          <a
            href="mailto:support@vawick.de"
            className="group glass rounded-2xl border border-[#152222] hover:border-[#9BB8B8]/30 p-6 transition-colors"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#9BB8B8] font-bold mb-3">
              Compliance
            </div>
            <div className="font-display text-xl font-medium mb-2">Talk to us</div>
            <p className="text-sm text-[#9BB8B8] mb-5">
              Security reviews, DPA countersigning, or custom compliance questions.
            </p>
            <div className="flex items-center gap-1.5 text-sm font-medium text-white group-hover:text-[#9BB8B8] transition-colors">
              Contact us{" "}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
          </a>
        </motion.div>

        <p className="text-center text-xs text-[#7A9B9B] font-mono mt-12">
          Security posture questions?{" "}
          <a href="mailto:support@vawick.de" className="hover:text-white transition-colors">
            support@vawick.de
          </a>
        </p>
      </main>
    </div>
  );
}
